<script setup lang="ts">
import { LOGIN_PROVIDER, LOGIN_PROVIDER_TYPE } from "@toruslabs/base-controllers";
import { storageAvailable } from "@toruslabs/openlogin-utils";
import { Button } from "@toruslabs/vue-components/Button";
import log from "loglevel";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

// Assets
import IconGoogle from "../../assets/claim/icon-google.svg";
import IconMetaMask from "../../assets/claim/icon-metamask.svg";
import useUtils from "../../composables/use-utils";
import MetamaskLoginFactory from "../../handlers/Auth/MetamaskFactory";
import WcLoginFactory from "../../handlers/Auth/WcLoginFactory";
import { useClaimStore } from "../../store/claimStore";
import { useControllerStore } from "../../store/controllerStore";
import { useToastStore } from "../../store/toastStore";
import {
  ADAPTER,
  ADAPTER_TYPE,
  EMAIL_CONSENT_SESSION_KEY,
  EXTERNAL_LOGIN_PROVIDER,
  MOCA_ID_JWT_KEY,
  MOCA_ID_PARTNER_JWT_KEY,
} from "../../utils/enums";
import { TOAST_TYPES } from "../../utils/interfaces";
// Components
import { Loader } from "../Loader";
// import IconWalletConnect from "../../assets/claim/icon-walletconnect.svg";

const route = useRoute();
const claimStore = useClaimStore();
const useController = useControllerStore();
const toastStore = useToastStore();
const { storageUtils } = useUtils();

const loginEmail = ref("");
const emptyEmail = ref(false);
const isLoading = ref(false);
const showMetamask = ref(false);
const emailConsent = computed(() => claimStore.emailConsent);

async function onSuccessLogin(adapterName: ADAPTER_TYPE) {
  const address = useController.torusState.PreferencesControllerState.selectedAddress;
  if (address) {
    if (storageAvailable("localStorage")) {
      localStorage.setItem(storageUtils.value.walletSessionKey, JSON.stringify({ adapter: adapterName, connected: true }));
    }
    await claimStore.checkEligibility();
  }
}

function saveDataOnSession() {
  if (storageAvailable("sessionStorage")) {
    sessionStorage.setItem(EMAIL_CONSENT_SESSION_KEY, claimStore.emailConsent.toString());
    if (route.query.token) sessionStorage.setItem(MOCA_ID_JWT_KEY, route.query.token as string);
    if (route.query.partner) sessionStorage.setItem(MOCA_ID_PARTNER_JWT_KEY, route.query.partner as string);
  }
}

async function onLogin(verifier: LOGIN_PROVIDER_TYPE) {
  try {
    isLoading.value = true;
    saveDataOnSession();
    await useController.triggerLogin({ loginProvider: verifier, login_hint: loginEmail.value, theme: useController.userTheme });
    await onSuccessLogin(ADAPTER.OPENLOGIN);
  } catch (error) {
    log.error(error);
  } finally {
    isLoading.value = false;
  }
}

async function onEmailLogin() {
  if (loginEmail.value === "") {
    emptyEmail.value = true;
    return;
  }
  await onLogin(LOGIN_PROVIDER.EMAIL_PASSWORDLESS);
}

async function onMetamaskLogin() {
  try {
    isLoading.value = true;
    const metamask = await MetamaskLoginFactory.getInstance({
      providerConfig: useController.networkProviderConfig,
      onLogout: useController.logout,
    });
    const provider = metamask.connnected ? metamask.provider : await metamask.connect();
    const { idToken } = await metamask.authenticateUser();
    await useController.walletLogin({ loginProvider: EXTERNAL_LOGIN_PROVIDER.METAMASK, provider, idToken });
    await onSuccessLogin(ADAPTER.METAMASK);
  } catch (error) {
    toastStore.setToastMessage({ message: "User rejected connection", type: TOAST_TYPES.ERROR });
  } finally {
    isLoading.value = false;
  }
}

// async function onWalletConnectLogin() {
//   try {
//     isLoading.value = true;
//     await WcLoginFactory.getInstance();
//     const provider = await WcLoginFactory.connect();
//     if (provider) {
//       await useController.walletLogin({ provider });
//       await onSuccessLogin(ADAPTER.WALLET_CONNECT);
//     }
//   } catch (error) {
//     log.error(error);
//   } finally {
//     isLoading.value = false;
//   }
// }

onMounted(async () => {
  const metamaskInstance = await MetamaskLoginFactory.getInstance({
    providerConfig: useController.networkProviderConfig,
    onLogout: useController.logout,
  });
  if (metamaskInstance.provider) showMetamask.value = true;
  await WcLoginFactory.getInstance({ providerConfig: useController.networkProviderConfig });
});
</script>

<template>
  <div v-if="isLoading" class="flex flex-col items-center justify-center min-h-[500px] gap-2">
    <Loader />
    <p class="text-white text-base font-semibold">Logging in...</p>
  </div>
  <div v-if="!isLoading">
    <div class="flex flex-col items-center gap-3">
      <p class="text-2xl text-white font-bold">Create your Realm Account</p>
      <p class="text-moca-green-2 text-sm text-center">
        Your Moca ID will be minted into your Realm Account.
        <br /><br />
        <span class="text-moca-gray-1 text-sm text-center font-medium">
          If no Moca account is detected, we will automatically create a account for you.
        </span>
      </p>
      <div class="flex flex-col gap-2 w-full">
        <Button
          class="!h-14 !rounded-full !bg-moca-transparent border-moca-button-border border !text-white !text-base !font-semibold"
          @on-click="onLogin(LOGIN_PROVIDER.GOOGLE)"
        >
          <div class="flex justify-center items-center gap-2">
            <img :src="IconGoogle" class="w-6 h-6" />
            Continue with Google
          </div>
        </Button>
        <div class="flex items-center justify-center w-full gap-5 px-2">
          <div class="h-px bg-moca-white-50 w-full"></div>
          <div class="text-sm text-moca-white-50 font-semibold">OR</div>
          <div class="h-px bg-moca-white-50 w-full"></div>
        </div>
        <input
          v-model="loginEmail"
          class="rounded-full border-2 border-black h-12 px-6 focus:border-moca-yellow focus-within:border-moca-yellow focus:outline-none"
          placeholder="Enter your email"
          @input="emptyEmail = false"
        />
        <p v-if="emptyEmail" class="text-moca-error-red">*Email can't be empty</p>
        <Button
          class="!h-14 !rounded-full !bg-moca-transparent border-moca-button-border border !py-4 !text-white !text-base !font-semibold"
          @on-click="onEmailLogin"
        >
          Continue with Email
        </Button>
        <div class="flex flex-row items-center justify-center gap-2">
          <input
            v-model="emailConsent"
            class="h-4 w-4 rounded-sm border-2 border-black accent-moca-yellow"
            type="checkbox"
            @change="
              () => {
                claimStore.emailConsent = !emailConsent;
              }
            "
          />
          <p class="text-base text-white font-semibold">Receive updates from Mocaverse.</p>
        </div>
        <div v-if="showMetamask" class="flex items-center justify-center w-full gap-5 px-2">
          <div class="h-px bg-moca-white-50 w-full"></div>
          <div class="text-sm text-moca-white-50 font-semibold">OR</div>
          <div class="h-px bg-moca-white-50 w-full"></div>
        </div>
        <Button
          v-if="showMetamask"
          class="!h-14 !rounded-full !bg-moca-transparent border-moca-button-border border !py-4 !text-white !text-base !font-semibold"
          @on-click="onMetamaskLogin()"
        >
          <div class="flex justify-center items-center gap-2">
            <img :src="IconMetaMask" class="w-6 h-6" />
            MetaMask
          </div>
        </Button>
      </div>
    </div>
    <p class="text-moca-gray-1 italic text-sm font-medium mt-8 px-2">
      Note: Your wallet will act as your signer/log in method for your new account. Full explanation
      <span class="font-bold">here</span>
    </p>
  </div>
</template>
